// MasterDataCollection.jsx — DRHP "Master Data" capture module.
// 1:1 port of `claude - drhp_master_data_collection module.html`, scoped under
// `.drhp-md-root`. Single source of truth for the most-repeated DRHP entities.
// Save & Sync writes values into every chapter placeholder; re-syncing over
// already-populated values prompts an "Erase & Update" confirmation first.
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api.js";

const SECTIONS = [
  { id: "company", label: "Company", icon: "ti-building", ct: 1187 },
  { id: "promoters", label: "Promoters", icon: "ti-crown", ct: 399 },
  { id: "directors", label: "Directors & KMP", icon: "ti-users", ct: 382 },
  { id: "advisors", label: "Advisors", icon: "ti-briefcase", ct: 651 },
  { id: "issue", label: "Issue & shares", icon: "ti-coin", ct: 253 },
  { id: "contacts", label: "Contact details", icon: "ti-address-book", ct: 260 },
];

const initials = (name) =>
  (name || "").split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "—";

const Field = ({ label, token, req, value, onChange, placeholder, testid }) => (
  <div className="fg">
    <label className="fl">
      {label} {token && <span className="tok">{token}</span>} {req && <span className="req">*</span>}
    </label>
    <input className="fi" value={value || ""} placeholder={placeholder || ""} data-testid={testid}
      onChange={(e) => onChange(e.target.value)} />
  </div>
);

export default function MasterDataCollection({ workspace, user, onGoLinkedMap }) {
  const [m, setM] = useState(null);
  const [section, setSection] = useState("company");
  const [busy, setBusy] = useState(false);
  const [eraseOpen, setEraseOpen] = useState(false);
  const [lastSync, setLastSync] = useState(null);

  useEffect(() => {
    api.getMaster(workspace).then(setM).catch(() => setM(null));
  }, [workspace]);

  const kpis = m?.kpis || { total: 2970, autofillable: 2108, filled: 0, empty: 0 };

  // ── mutators (immutable state updates) ──
  const setObj = (key, field, val) => setM((p) => ({ ...p, [key]: { ...(p[key] || {}), [field]: val } }));
  const setRow = (key, idx, field, val) =>
    setM((p) => { const arr = [...(p[key] || [])]; arr[idx] = { ...arr[idx], [field]: val }; return { ...p, [key]: arr }; });
  const addRow = (key, blank) => setM((p) => ({ ...p, [key]: [...(p[key] || []), { id: `tmp-${Date.now()}`, ...blank }] }));
  const delRow = (key, idx) => setM((p) => { const arr = [...(p[key] || [])]; arr.splice(idx, 1); return { ...p, [key]: arr }; });

  const payload = useMemo(() => m && ({
    company: m.company || {}, promoters: m.promoters || [], directors: m.directors || [],
    advisors: m.advisors || [], issue: m.issue || {}, contacts: m.contacts || {},
  }), [m]);

  async function persist() {
    const res = await api.saveMaster(workspace, payload);
    if (res?.kpis) setM((p) => ({ ...p, kpis: res.kpis }));
    return res;
  }

  // Save & sync — re-sync over existing data prompts a confirmation first.
  async function onSaveSync() {
    if (busy || !m) return;
    setBusy(true);
    try {
      await persist();
      if (m.synced_at) { setEraseOpen(true); setBusy(false); return; }
      await doSync();
    } catch { toast.error("Could not save master data."); setBusy(false); }
  }

  async function doSync() {
    setBusy(true); setEraseOpen(false);
    try {
      const res = await api.syncMaster(workspace, { userId: user?.id, userName: user?.name });
      if (res?.kpis) setM((p) => ({ ...p, kpis: res.kpis, synced_at: new Date().toISOString() }));
      setLastSync(res);
      const msg = res?.rewritten
        ? `Synced — ${res.replaced} placeholder(s) populated, ${res.rewritten} rewritten.`
        : `Synced — ${res.replaced} placeholder(s) populated across the DRHP.`;
      toast.success(msg);
    } catch { toast.error("Sync failed."); }
    setBusy(false);
  }

  async function onReviewApprove() {
    if (!m) return;
    setBusy(true);
    try {
      await persist();
      await api.approveMaster(workspace, { userId: user?.id, userName: user?.name });
      onGoLinkedMap(true);
    } catch { toast.error("Could not approve."); }
    setBusy(false);
  }

  if (!m) return <div className="drhp-md-root"><div style={{ padding: 28, fontSize: 13, color: "#8a8a94" }}>Loading master data…</div></div>;

  const c = m.company || {}, iss = m.issue || {}, ct = m.contacts || {};

  return (
    <div className="drhp-md-root" data-testid="drhp-master-data">
      <div className="md-bar">
        <div className="md-logo"><i className="ti ti-database" style={{ color: "#fff", fontSize: 17 }} /></div>
        <div>
          <div className="md-title">DRHP Master Data</div>
          <div className="md-sub-t">Capture once · auto-populate everywhere</div>
        </div>
        <button className="md-save" onClick={onSaveSync} disabled={busy} data-testid="md-save-sync-btn">
          <i className="ti ti-refresh" style={{ fontSize: 12 }} /> {busy ? "Working…" : "Save & sync to DRHP"}
        </button>
      </div>

      <div className="md-kpis">
        <div className="kk"><div className="kk-v p">{kpis.total.toLocaleString()}</div><div className="kk-l">Total placeholders in DRHP</div></div>
        <div className="kk"><div className="kk-v p">{kpis.autofillable.toLocaleString()}</div><div className="kk-l">Auto-fillable from master data</div></div>
        <div className="kk"><div className="kk-v" data-testid="md-kpi-filled">{kpis.filled.toLocaleString()}</div><div className="kk-l">Filled &amp; synced now</div></div>
        <div className="kk"><div className="kk-v r">{kpis.empty.toLocaleString()}</div><div className="kk-l">Master fields still empty</div></div>
      </div>

      <div className="md-body">
        <div className="nav">
          <div className="nav-lbl">Entities</div>
          {SECTIONS.map((s) => (
            <div key={s.id} className={`nav-i ${section === s.id ? "on" : ""}`} onClick={() => setSection(s.id)} data-testid={`md-nav-${s.id}`}>
              <i className={`ti ${s.icon}`} /> {s.label} <span className="ct">{s.ct}</span>
            </div>
          ))}
          <div className="nav-lbl" style={{ marginTop: 10 }}>Tools</div>
          <div className="nav-i" onClick={() => onGoLinkedMap(false)} data-testid="md-nav-linked-map"><i className="ti ti-link" /> Linked fields map</div>
          <div className="nav-i" onClick={() => onGoLinkedMap(false)} data-testid="md-nav-consistency"><i className="ti ti-checkup-list" /> Consistency check</div>
        </div>

        <div className="pane">
          {section === "company" && (
            <>
              <div className="pane-h">Company / Issuer details</div>
              <div className="pane-d">The most-repeated entity in your DRHP. Captured once here, populated everywhere the issuer is named.</div>
              <span className="usage"><i className="ti ti-refresh" style={{ fontSize: 11 }} /> Auto-fills 1,187 references across all 39 chapters</span>
              <Field label="Registered legal name" token="{{company.name}}" req value={c.name} placeholder="e.g. Acme Seeds Limited" onChange={(v) => setObj("company", "name", v)} testid="md-company-name" />
              <div className="row2">
                <Field label="CIN" token="{{company.cin}}" value={c.cin} placeholder="U01100MH..." onChange={(v) => setObj("company", "cin", v)} testid="md-company-cin" />
                <Field label="Date of incorporation" token="{{company.doi}}" value={c.doi} onChange={(v) => setObj("company", "doi", v)} />
              </div>
              <Field label="Registered office address" token="{{company.regd_office}}" req value={c.regd_office} onChange={(v) => setObj("company", "regd_office", v)} testid="md-company-regd" />
              <div className="row3">
                <Field label="Email" token="{{company.email}}" value={c.email} onChange={(v) => setObj("company", "email", v)} />
                <Field label="Phone" token="{{company.phone}}" value={c.phone} onChange={(v) => setObj("company", "phone", v)} />
                <Field label="Website" token="{{company.web}}" value={c.web} onChange={(v) => setObj("company", "web", v)} />
              </div>
              <div className="preview">
                <div className="preview-t"><i className="ti ti-eye" style={{ fontSize: 11 }} /> Live preview — how it appears in the DRHP</div>
                <div className="preview-b">"Our Company was incorporated as <span className="hl">{c.name || "[●]"}</span> on <span className="hl">{c.doi || "[●]"}</span>… having its registered office at <span className="hl">{c.regd_office || "[●]"}</span> (CIN: <span className="hl">{c.cin || "[●]"}</span>)."</div>
              </div>
            </>
          )}

          {section === "promoters" && (
            <>
              <div className="pane-h">Promoters</div>
              <div className="pane-d">Add each promoter once. Their name, PAN, address and shareholding flow into the Promoter, Capital Structure and Risk chapters.</div>
              <span className="usage"><i className="ti ti-refresh" style={{ fontSize: 11 }} /> Auto-fills 399 references across Promoter, Capital Structure &amp; Risk chapters</span>
              {(m.promoters || []).map((p, idx) => (
                <div className="entity" key={p.id || idx} data-testid={`md-promoter-${idx}`}>
                  <div className="entity-top">
                    <div className="entity-av">{initials(p.name)}</div>
                    <div><div className="entity-n">{p.name || "New promoter"}</div><div className="entity-r">Individual Promoter · {`{{promoter.${idx + 1}}}`}</div></div>
                    <span className="entity-del" onClick={() => delRow("promoters", idx)} data-testid={`md-promoter-del-${idx}`}><i className="ti ti-trash" /></span>
                  </div>
                  <div className="row3">
                    <Field label="Full name" value={p.name} onChange={(v) => setRow("promoters", idx, "name", v)} testid={`md-promoter-name-${idx}`} />
                    <Field label="PAN" value={p.pan} onChange={(v) => setRow("promoters", idx, "pan", v)} />
                    <Field label="DIN" value={p.din} onChange={(v) => setRow("promoters", idx, "din", v)} />
                  </div>
                  <Field label="Address" value={p.address} onChange={(v) => setRow("promoters", idx, "address", v)} />
                </div>
              ))}
              <button className="add-btn" onClick={() => addRow("promoters", { name: "", pan: "", din: "", address: "" })} data-testid="md-add-promoter">+ Add promoter</button>
            </>
          )}

          {section === "directors" && (
            <>
              <div className="pane-h">Directors &amp; Key Managerial Personnel</div>
              <div className="pane-d">Board and KMP captured once — names, DIN, designation and tenure populate Our Management, the cover and signature blocks.</div>
              <span className="usage"><i className="ti ti-refresh" style={{ fontSize: 11 }} /> Auto-fills 382 references across Management &amp; General Information</span>
              {(m.directors || []).map((d, idx) => (
                <div className="entity" key={d.id || idx} data-testid={`md-director-${idx}`}>
                  <div className="entity-top">
                    <div className="entity-av">{initials(d.name)}</div>
                    <div><div className="entity-n">{d.name || "New director"}</div><div className="entity-r">{d.designation || "Director"} · {`{{director.${idx + 1}}}`}</div></div>
                    <span className="entity-del" onClick={() => delRow("directors", idx)} data-testid={`md-director-del-${idx}`}><i className="ti ti-trash" /></span>
                  </div>
                  <div className="row3">
                    <Field label="Name" value={d.name} onChange={(v) => setRow("directors", idx, "name", v)} testid={`md-director-name-${idx}`} />
                    <Field label="Designation" value={d.designation} onChange={(v) => setRow("directors", idx, "designation", v)} />
                    <Field label="DIN" value={d.din} onChange={(v) => setRow("directors", idx, "din", v)} />
                  </div>
                  <div className="row2">
                    <Field label="Membership no." value={d.membership} onChange={(v) => setRow("directors", idx, "membership", v)} />
                    <Field label="Email" value={d.email} onChange={(v) => setRow("directors", idx, "email", v)} />
                  </div>
                </div>
              ))}
              <button className="add-btn" onClick={() => addRow("directors", { name: "", designation: "", din: "", membership: "", email: "" })} data-testid="md-add-director">+ Add director / KMP</button>
            </>
          )}

          {section === "advisors" && (
            <>
              <div className="pane-h">Advisors &amp; intermediaries</div>
              <div className="pane-d">BRLM, registrar, auditor and bankers — each appears on the cover, General Information and throughout Issue chapters.</div>
              <span className="usage"><i className="ti ti-refresh" style={{ fontSize: 11 }} /> Auto-fills 651 references — BRLM alone appears 478 times</span>
              {(m.advisors || []).map((a, idx) => (
                <div className="entity" key={a.id || idx} data-testid={`md-advisor-${idx}`}>
                  <div className="entity-top">
                    <div className="entity-av">{initials(a.name)}</div>
                    <div><div className="entity-n">{a.name || "New advisor"}</div><div className="entity-r">{a.role || "Intermediary"}</div></div>
                    <span className="entity-del" onClick={() => delRow("advisors", idx)} data-testid={`md-advisor-del-${idx}`}><i className="ti ti-trash" /></span>
                  </div>
                  <div className="row2">
                    <Field label="Name" value={a.name} onChange={(v) => setRow("advisors", idx, "name", v)} testid={`md-advisor-name-${idx}`} />
                    <Field label="Role" value={a.role} onChange={(v) => setRow("advisors", idx, "role", v)} />
                  </div>
                  <div className="row2">
                    <Field label="SEBI registration" value={a.sebi_reg} onChange={(v) => setRow("advisors", idx, "sebi_reg", v)} />
                    <Field label="Contact / email" value={a.contact} onChange={(v) => setRow("advisors", idx, "contact", v)} />
                  </div>
                </div>
              ))}
              <button className="add-btn" onClick={() => addRow("advisors", { name: "", role: "", sebi_reg: "", contact: "" })} data-testid="md-add-advisor">+ Add advisor / intermediary</button>
            </>
          )}

          {section === "issue" && (
            <>
              <div className="pane-h">Issue &amp; share details</div>
              <div className="pane-d">Face value, issue size and share counts repeat across The Issue, Capital Structure and Issue Procedure chapters.</div>
              <span className="usage"><i className="ti ti-refresh" style={{ fontSize: 11 }} /> Auto-fills 253 references across Issue &amp; Capital Structure chapters</span>
              <div className="row2">
                <Field label="Face value per share" token="{{issue.fv}}" value={iss.fv} onChange={(v) => setObj("issue", "fv", v)} testid="md-issue-fv" />
                <Field label="Issue size" token="{{issue.size}}" value={iss.size} onChange={(v) => setObj("issue", "size", v)} />
              </div>
              <div className="row2">
                <Field label="Fresh issue shares" token="{{issue.fresh}}" value={iss.fresh} onChange={(v) => setObj("issue", "fresh", v)} />
                <Field label="Listing exchange" token="{{issue.exch}}" value={iss.exch} onChange={(v) => setObj("issue", "exch", v)} />
              </div>
            </>
          )}

          {section === "contacts" && (
            <>
              <div className="pane-h">Contact details</div>
              <div className="pane-d">Emails, phones and websites for the company, registrar and investor grievance — repeated on the cover and across Issue chapters.</div>
              <span className="usage"><i className="ti ti-refresh" style={{ fontSize: 11 }} /> Auto-fills 260 references (emails, phones, websites)</span>
              <div className="row2">
                <Field label="Investor grievance email" token="{{grievance.email}}" value={ct.grievance_email} onChange={(v) => setObj("contacts", "grievance_email", v)} testid="md-contact-grievance" />
                <Field label="Registrar grievance email" token="{{rta.email}}" value={ct.rta_email} onChange={(v) => setObj("contacts", "rta_email", v)} />
              </div>
              <div className="preview">
                <div className="preview-t"><i className="ti ti-eye" style={{ fontSize: 11 }} /> Why one place matters</div>
                <div className="preview-b">A single typo in a phone number, repeated across the cover, General Information and Issue Procedure, is a common SEBI observation. Captured once, it's correct everywhere.</div>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="md-actions">
        <span className="md-status" data-testid="md-status">
          {m.synced_at
            ? <><i className="ti ti-circle-check" /> <span className="ok">Last synced{lastSync ? ` · ${lastSync.replaced} placeholders` : ""}</span></>
            : <><i className="ti ti-info-circle" /> Not synced yet — save & sync to populate the DRHP</>}
        </span>
        <span className="spacer" />
        <button className="ma-btn" onClick={onSaveSync} disabled={busy} data-testid="md-sync-again-btn">
          <i className="ti ti-refresh" /> Sync again
        </button>
        <button className="ma-btn pri" onClick={onReviewApprove} disabled={busy} data-testid="md-review-approve-btn">
          Review &amp; Approve <i className="ti ti-arrow-right" />
        </button>
      </div>

      {eraseOpen && (
        <div className="md-modal-ov" data-testid="md-erase-modal" onClick={() => setEraseOpen(false)}>
          <div className="md-modal" onClick={(e) => e.stopPropagation()}>
            <div className="md-modal-ic"><i className="ti ti-alert-triangle" /></div>
            <div className="md-modal-h">Erase &amp; update synced data?</div>
            <div className="md-modal-b">
              This DRHP was already populated from master data. Re-syncing will <b>erase the previously written values</b> and
              rewrite them with your current master data across every chapter. This cannot be undone automatically.
            </div>
            <div className="md-modal-actions">
              <button className="ma-btn" onClick={() => setEraseOpen(false)} data-testid="md-erase-cancel">Cancel</button>
              <button className="ma-btn pri danger" onClick={doSync} data-testid="md-erase-confirm">
                <i className="ti ti-refresh" /> Erase &amp; update
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
